import { FC } from 'react';
import {
  IGeneralData,
  IProductDelivery,
  IproductReferenceDataInCountry,
  IShipmentData
} from '../../../../../../../types/orders';
import TrackingAndDatesSection from '../../../Tab1General/Components/TrackingAndDatesSection/TrackingAndDatesSection';
import { IconEditOrders } from '../../../../../../../assets/DesignSystem/SVGComponents';
import { NudosCareTag, NudosHoverText } from '../../../../../../../components/NudosComponents';
import { CopyIcon } from 'assets/images/shopping';
import DeliveryProcessModal from '../../../../../Components/DeliveryProcessModal/DeliveryProcessModal';
import SerialsModal from '../../../../../Components/SerialsModal/SerialsModal';
import NewEditSkuModal from '../../../../../Components/EditSkuModal/NewEditSkuModal';
import { ModalMoveWithPendingConfiguration, PendingSettingsIndicator } from '../../../../../../../components';
import ShipmentStatusTransitionModal from '../../../../../Components/ShipmentStatusTransitionModal/ShipmentStatusTransitionModal';
import TransitToWarehouseReasonModal from '../../../../../Components/TransitToWarehouseReasonModal/TransitToWarehouseReasonModal';
import NudosConfirmActionModal from '../../../../../../../components/NudosComponents/NudosConfirmActionModal/NudosConfirmActionModal';
import { formatSpecs } from '../../../../../../../utils/formatSpecs';
import BordSelectDropdown from 'components/NudosComponents/BordSelectDropdown/BordSelectDropdown';
import { useShipmentCardTracking } from './useShipmentCardTracking';
import './ShipmentCard.scss';

interface IShipmentCardTrackingProps {
  listOfGroupedShipments: IShipmentData[];
  completeDataForEachGroupOfShipments: IShipmentData;
  productsInShippingGroup: IProductDelivery[];
  isEnabled: boolean;
  index: number;
  orderId: number;
  generalData: IGeneralData;
  updateWithoutLoaders?: () => Promise<void>;
  updateWithLoaders?: () => Promise<void>;
  references: IproductReferenceDataInCountry[];
  optionalShipmentTitle?: { current: number; total: number };
  isLastCardInRenderedList?: boolean;
}

const ShipmentCardTracking: FC<IShipmentCardTrackingProps> = ({
  completeDataForEachGroupOfShipments,
  productsInShippingGroup,
  isEnabled,
  index,
  listOfGroupedShipments,
  orderId,
  generalData,
  updateWithoutLoaders,
  updateWithLoaders,
  references,
  optionalShipmentTitle,
  isLastCardInRenderedList
}: IShipmentCardTrackingProps) => {
  const {
    idLoad,
    setShowMessage,
    selectOption,
    showProcessModal,
    setShowProcessModal,
    showProcessModalConfirmation,
    setShowProcessModalConfirmation,
    isEditedProcesModal,
    setIsEditedProcessModal,
    editSkuModal,
    setEditSkuModal,
    loadingSku,
    notFoundSku,
    showPendingConfigurationModal,
    setShowPendingConfigurationModal,
    showSerialsModal,
    setShowSerialsModal,
    showShipmentStatusTransitionModal,
    transitionConfigForModal,
    productInformationForThisShipment,
    totalAdditionalSettings,
    lastItemInTheList,
    hasUnconfirmedActions,
    countryTimeZone,
    singleProduct,
    allowSkuEditing,
    isStockReservation,
    hasPendingConfigMessage,
    handleConfirmPendingConfiguration,
    generateOptions,
    handleChangeFoundSku,
    updateSKU,
    handleCopyProductInfo,
    getSerialActionText,
    handleTransitionConfirmed,
    isShipmentStatusSelectDeactivated,
    productsWithResolvedSerials,
    listOfShipmentsAvailableToAddForTransition,
    shouldShowPaymentFloatMessage,
    handleShipmentStatusTransitionModalClose,
    handleEditSkuClick,
    showTransitToWarehouseReasonModal,
    setShowTransitToWarehouseReasonModal,
    handleTransitToWarehouseReasonConfirm,
    pendingWarehouseStopReasonsForTransition,
    transitToWarehouseModalInitialReasons,
    handleOpenEditTransitToWarehouseFromTracking
  } = useShipmentCardTracking({
    listOfGroupedShipments,
    completeDataForEachGroupOfShipments,
    productsInShippingGroup,
    isEnabled,
    index,
    orderId,
    generalData,
    updateWithoutLoaders,
    updateWithLoaders,
    references,
    isLastCardInRenderedList
  });

  return (
    <div className="shipmentCard">
      {showTransitToWarehouseReasonModal && (
        <TransitToWarehouseReasonModal
          setShowModal={setShowTransitToWarehouseReasonModal}
          onConfirm={handleTransitToWarehouseReasonConfirm}
          initialSelectedReasons={transitToWarehouseModalInitialReasons}
        />
      )}
      {showShipmentStatusTransitionModal && transitionConfigForModal && (
        <ShipmentStatusTransitionModal
          setShowModal={handleShipmentStatusTransitionModalClose}
          shipmentData={completeDataForEachGroupOfShipments}
          products={productsWithResolvedSerials}
          onTransitionConfirmed={handleTransitionConfirmed}
          transitionConfig={transitionConfigForModal}
          listOfShipmentsAvailableToAdd={listOfShipmentsAvailableToAddForTransition}
          updateWithoutLoaders={updateWithoutLoaders}
          orderId={String(orderId)}
          organizationName={generalData?.orgData?.organizationName || ''}
          receiverName={completeDataForEachGroupOfShipments?.receiverInfo?.receiverName}
          references={references}
          warehouseStopReasons={pendingWarehouseStopReasonsForTransition ?? undefined}
          optionalShipmentTitle={optionalShipmentTitle}
        />
      )}
      {showSerialsModal && (
        <SerialsModal
          productInfo={singleProduct}
          singleProduct={showSerialsModal}
          changeShowModal={() => setShowSerialsModal(undefined)}
          updateWithoutLoaders={updateWithoutLoaders}
          products={productsInShippingGroup}
          references={references}
          customDisabledButton={isStockReservation}
        />
      )}
      {editSkuModal && (
        <NewEditSkuModal
          defaultSku={editSkuModal?.sku || ''}
          handleChangeFoundSku={() => handleChangeFoundSku(false)}
          notFoundSku={notFoundSku}
          closeModal={() => {
            handleChangeFoundSku(false);
            setEditSkuModal(undefined);
          }}
          updateSKU={(newSku: string) => updateSKU(newSku)}
          loadingSku={loadingSku}
          productInfo={{ name: editSkuModal?.name, pills: editSkuModal?.pills, units: productsInShippingGroup?.length }}
        />
      )}
      {showProcessModalConfirmation && (
        <NudosConfirmActionModal
          modalText="¿Estás seguro que quieres cambiar de estado? Se actualizara en el WMS para indicar que la herramienta salió de bodega y va hacia el empleado."
          firstButtonText="No, cancelar"
          secondButtonText="Si continuar"
          firstButtonAction={() => setShowProcessModalConfirmation(false)}
          secondButtonAction={() => {
            setShowProcessModal(true);
            setShowProcessModalConfirmation(false);
          }}
          customHeight="250px"
        />
      )}
      {showProcessModal && (
        <DeliveryProcessModal
          changeShowModal={() => setShowProcessModal(false)}
          updateDataTraking={updateWithoutLoaders}
          dataTraking={completeDataForEachGroupOfShipments}
          isEdited={isEditedProcesModal}
          listOfGroupedShipments={listOfGroupedShipments}
          orderId={orderId}
          generalData={generalData}
          receiverName={completeDataForEachGroupOfShipments?.receiverInfo?.receiverName}
          flag={completeDataForEachGroupOfShipments?.country?.flag}
          warehouseTemporaryStorage={completeDataForEachGroupOfShipments?.warehouseTemporaryStorage}
          selectOption={selectOption}
          references={references}
        />
      )}
      {(optionalShipmentTitle && (
        <div className="shipmentCardHeader">
          <div className="productTitleContainer">
            <div className="unitsOfTheProductTitle">
              {`Envío ${optionalShipmentTitle.current} de ${optionalShipmentTitle.total}`}
            </div>
          </div>
        </div>
      )) ||
        null}
      <div className="cardItemsContainer">
        <div className="leftSection">
          <div className="unitsContainer">
            <div className="grayTitle">Und.</div>
            <div className="valueText">{productsInShippingGroup?.length}</div>
          </div>

          <div className="infoProductDetailsContainer">
            <div className="grayTitle">Producto</div>
            <div
              className="textProductDetails"
              style={{ color: productInformationForThisShipment?.isRequestedChange ? '#999999' : '' }}
            >
              <div className="textLimits">{productInformationForThisShipment?.name}</div>
              <NudosCareTag
                nudosCareType={productInformationForThisShipment?.nudosCareName}
                customHeight="18px"
                customFontSize="8px"
              />
              <NudosHoverText
                text={formatSpecs(productInformationForThisShipment?.pills)}
                charactersLimit={30}
                onlyIfTruncated
                customTextHoverClassName="customTextHoverClassName"
              />
              <div className="shipmentCopyIconWrapper" onClick={handleCopyProductInfo}>
                <CopyIcon stroke="#383838" />

                <div className="shipmentCopyIconTooltip">Copiar información del producto</div>
              </div>
            </div>
            <div
              className="skuProduct sizeFontProductDetails"
              style={{ color: productInformationForThisShipment?.isRequestedChange ? '#999999' : '' }}
            >
              <span> SKU Catalogo: {productInformationForThisShipment?.sku}</span>
              {allowSkuEditing && (
                <div
                  className="editAction"
                  style={{ cursor: productInformationForThisShipment?.isRequestedChange ? 'not-allowed' : 'pointer' }}
                  onClick={handleEditSkuClick}
                >
                  <IconEditOrders />
                </div>
              )}
            </div>
            <PendingSettingsIndicator totalAdditionalSettings={totalAdditionalSettings} />
            <ModalMoveWithPendingConfiguration
              showPendingConfigurationModal={showPendingConfigurationModal}
              setShowPendingConfigurationModal={setShowPendingConfigurationModal}
              message={hasPendingConfigMessage(hasUnconfirmedActions)}
              onConfirmContinue={handleConfirmPendingConfiguration}
              primaryButtonText="Sí, pasar a Preparación"
            />
            <div
              className="addSerials"
              onClick={() => {
                setShowSerialsModal(productInformationForThisShipment);
              }}
            >
              <span className="addSerialsText">{getSerialActionText()}</span>
              <div
                className="editAction"
                style={{ cursor: productInformationForThisShipment?.isRequestedChange ? 'not-allowed' : 'pointer' }}
              >
                <IconEditOrders />
              </div>
            </div>
          </div>

          <div className="shipmentStatus">
            <div className="grayTitle">Estado</div>
            <div
              className="boxStatus"
              onMouseOut={() => !isEnabled && !idLoad && setShowMessage(true)}
              onMouseLeave={() => setShowMessage(false)}
            >
              <BordSelectDropdown
                isFilled
                isLoading={idLoad === completeDataForEachGroupOfShipments?.shipmentId}
                isDeactivated={isShipmentStatusSelectDeactivated}
                selectOptions={generateOptions(completeDataForEachGroupOfShipments)}
                currentValueOrplaceholder={selectOption?.name || 'Por confirmar'}
              />
              {shouldShowPaymentFloatMessage && (
                <div className="floatMessage">
                  Para cambiar el estado debemos recibir el pago. En caso de ser un método de pago manual cambiálo en el
                  Trello cargando el comprobante
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="rightSection">
          <div className="grayTitle">Información de tracking</div>
          <TrackingAndDatesSection
            shipmentData={completeDataForEachGroupOfShipments as IShipmentData}
            countryTimeZone={countryTimeZone}
            setShowProcessModal={setShowProcessModal}
            setIsEditedProcessModal={setIsEditedProcessModal}
            onEditInboundTransitClick={handleOpenEditTransitToWarehouseFromTracking}
          />
        </div>
      </div>
      {productInformationForThisShipment?.isRequestedChange && <div className="alertSku">En proceso de cambio </div>}
      {!lastItemInTheList && <div className="separationLine" />}
    </div>
  );
};

export default ShipmentCardTracking;
